import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "EligibilitySolutionVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: '.sheet',
      required: false
    },
    eligibility_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        personal_information: false,
        ivs_rules: false,
        policy_filters: false,
        scorecards: false,
        contact_details: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some(option => option);
    },
    safe_eligibility_data() {
      return this.eligibility_data || {};
    },
    
    // Application Output helpers
    application_output() {
      return this.safe_eligibility_data.ApplicationOutput || {};
    },
    
    // Basic Information
    application_id() {
      return this.application_output.ApplicationID || 'N/A';
    },
    
    first_name() {
      return this.application_output.FirstName || '';
    },
    
    surname() {
      return this.application_output.Surname || '';
    },
    
    full_name() {
      return `${this.first_name} ${this.surname}`.trim() || 'N/A';
    },
    
    id_number() {
      return this.application_output.IDNumber || 'N/A';
    },
    
    industry() {
      return this.application_output.Industry || 'N/A';
    },
    
    industry_outcome() {
      return this.application_output.IndustryOutcome || 'N/A';
    },
    
    overall_outcome() {
      return this.application_output.Outcome || 'N/A';
    },
    
    reason_code() {
      return this.application_output.ReasonCode || 'N/A';
    },
    
    reason_description() {
      return this.application_output.ReasonDescription || 'N/A';
    },
    
    outcome_message() {
      return this.application_output.OutcomeMessage || 'N/A';
    },
    
    verification_status() {
      return this.application_output.Status || 'N/A';
    },
    
    subscriber_code() {
      return this.application_output.SubscriberCode || 'N/A';
    },
    
    // IVS Rules
    ivs_rules() {
      return this.application_output.IVSRules || {};
    },
    
    ivs_reason_code() {
      return this.ivs_rules.ReasonCode || 'N/A';
    },
    
    ivs_rules_list() {
      const rules = { ...this.ivs_rules };
      delete rules.ReasonCode; // Remove ReasonCode from the list
      return rules;
    },
    
    // Policy Filters
    policy_filters() {
      return this.application_output.PolicyFilters || {};
    },
    
    policy_filter_industry() {
      return Object.keys(this.policy_filters)[0] || 'N/A';
    },
    
    policy_filter_data() {
      const industry_key = this.policy_filter_industry;
      return this.policy_filters[industry_key] || {};
    },
    
    policy_filter_outcome() {
      return this.policy_filter_data.Outcome || 'N/A';
    },
    
    policy_filters_list() {
      return this.policy_filter_data.PolicyFilter || [];
    },
    
    // Scorecards
    scorecards() {
      return this.application_output.Scorecards || {};
    },
    
    scorecard() {
      return this.scorecards.Scorecard || {};
    },
    
    scorecard_name() {
      return this.scorecard.Name || 'N/A';
    },
    
    scorecard_score() {
      return this.scorecard.Score || 'N/A';
    },
    
    scorecard_outcome() {
      return this.scorecard.Outcome || 'N/A';
    },
    
    // Contact Details
    contact_details() {
      return this.application_output.ContactDetails || {};
    },
    
    email_address() {
      return this.contact_details.EmailAddress || null;
    },

    home_phone_number() {
      return this.contact_details.HomeTelephoneNumber?.Landline || null;
    },
    
    addresses() {
      const address_data = this.contact_details.Address || {};
      return address_data.Address || [];
    },
    
    cell_numbers() {
      const cell_data = this.contact_details.CellNumber || {};
      return cell_data.CellNumber || [];
    },
    
    work_telephone() {
      const work_tel = this.contact_details.WorkTelephoneNumber || {};
      return work_tel.Landline || null;
    },
    
    // Date formatting
    generated_date() {
      return new Date().toISOString();
    },
    
    formatted_generated_date() {
      return this.formatDate(this.generated_date);
    },
    
    report_reference() {
      return this.application_id;
    },
    
    // Badge classes
    outcome_badge_class() {
      return this.overall_outcome === 'Pass' ? 'status-good' : 'status-bad';
    },
    
    outcome_icon() {
      return this.overall_outcome === 'Pass' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    
    score_color_class() {
      const score = parseInt(this.scorecard_score);
      if (score >= 700) return 'text-success';
      if (score >= 500) return 'text-warning';
      return 'text-danger';
    },
    modal_instance() {
      var modal = new window.bootstrap.Modal(document.getElementById(`export-modal-${this.report_reference}`))
      return modal;
    }
  },
  
  methods: {
    close_modal() {
      this.modal_instance.hide();
    },
    // Utility methods
    formatDate(date) {
      return format_date(date);
    },
    
    format_address_line(address) {
      let address_line = '';
      if (address.Line1 && address.Line1 !== 'None') {
        address_line += address.Line1;
      }
      if (address.Line2 && address.Line2 !== 'None') {
        address_line += address_line ? ', ' + address.Line2 : address.Line2;
      }
      return address_line || 'N/A';
    },
    
    get_rule_badge_class(outcome) {
      return outcome === 'False' ? 'bg-success' : 'bg-danger';
    },
    
    get_rule_icon(outcome) {
      return outcome === 'False' ? 'fas fa-check' : 'fas fa-times';
    },
    
    get_policy_outcome_badge_class(outcome) {
      return outcome === 'Pass' ? 'status-good' : 'status-bad';
    },
    
    get_policy_outcome_icon(outcome) {
      return outcome === 'Pass' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    
    get_scorecard_outcome_badge_class(outcome) {
      return outcome === 'Pass' ? 'status-good' : 'status-bad';
    },
    
    get_scorecard_outcome_icon(outcome) {
      return outcome === 'Pass' ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    
    // Export functionality
    show_export_modal() {
      this.modal_instance.show();
    },
    
    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      }
      
      if (!this.libraries_loaded.sheetjs) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      }
    },
    
    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
      async export_data() {
      await this.load_export_libraries();
      
      
      if (this.export_format === 'csv') {
        await this.export_csv();
      } else if (this.export_format === 'xlsx') {
        await this.export_excel();
      } else if (this.export_format === 'json') {
        await this.export_json();
      }
      
      // Close modal
      this.close_modal();
    },
    
    // Prepare personal information data
    prepare_personal_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Application ID": this.application_id,
          "Full Name": this.full_name,
          "First Name": this.first_name,
          "Surname": this.surname,
          "ID Number": this.id_number,
          "Industry": this.industry,
          "Industry Outcome": this.industry_outcome,
          "Overall Outcome": this.overall_outcome,
          "Verification Status": this.verification_status,
          "Reason Code": this.reason_code,
          "Reason Description": this.reason_description,
          "Outcome Message": this.outcome_message,
          "Subscriber Code": this.subscriber_code,
          "Generated Date": this.formatted_generated_date
        }
      ];
    },
    
    // Prepare IVS Rules data
    prepare_ivs_rules_data() {
      const rules_data = [];
      
      // Add reason code first
      rules_data.push({
        "Rule Type": "IVS Reason Code",
        "Rule Name": "Overall Reason Code",
        "Description": "IVS verification reason code",
        "Outcome": this.ivs_reason_code,
        "Status": this.ivs_reason_code !== 'N/A' ? 'Available' : 'Not Available'
      });
      
      // Add individual rules
      Object.entries(this.ivs_rules_list).forEach(([rule_key, rule]) => {
        rules_data.push({
          "Rule Type": "IVS Rule",
          "Rule Name": rule_key,
          "Description": rule.Description || 'N/A',
          "Outcome": rule.Outcome || 'N/A',
          "Status": rule.Outcome === 'False' ? 'Passed' : 'Failed'
        });
      });
      
      return rules_data;
    },
    
    // Prepare Policy Filters data
    prepare_policy_filters_data() {
      const filters_data = [];
      
      // Add filter header information
      filters_data.push({
        "Filter Type": "Policy Filter Header",
        "Industry": this.policy_filter_industry,
        "Overall Outcome": this.policy_filter_outcome,
        "Filter Name": "Industry Filter",
        "Description": `Policy filter for ${this.policy_filter_industry} industry`,
        "Result": this.policy_filter_outcome,
        "Status": this.policy_filter_outcome === 'Pass' ? 'Passed' : 'Failed'
      });
      
      // Add individual filter rules
      this.policy_filters_list.forEach((filter, index) => {
        filters_data.push({
          "Filter Type": "Policy Filter Rule",
          "Industry": this.policy_filter_industry,
          "Overall Outcome": this.policy_filter_outcome,
          "Filter Name": `Filter ${index + 1}`,
          "Description": filter.Description || 'N/A',
          "Result": filter.Outcome || 'N/A',
          "Status": filter.Outcome === 'False' ? 'Passed' : 'Failed'
        });
      });
      
      return filters_data;
    },
    
    // Prepare Scorecards data
    prepare_scorecards_data() {
      return [
        {
          "Scorecard Name": this.scorecard_name,
          "Score": this.scorecard_score,
          "Outcome": this.scorecard_outcome,
          "Status": this.scorecard_outcome === 'Pass' ? 'Passed' : 'Failed',
          "Score Range": this.get_score_range(this.scorecard_score),
          "Risk Level": this.get_risk_level(this.scorecard_score),
          "Generated Date": this.formatted_generated_date
        }
      ];
    },
    
    // Prepare Contact Details data
    prepare_contact_details_data() {
      const contact_data = [];
      
      // Add email if available
      if (this.email_address) {
        contact_data.push({
          "Contact Type": "Email",
          "Contact Value": this.email_address,
          "Information Date": "N/A",
          "Status": "Available"
        });
      }
      
      // Add home telephone if available
      if (this.home_phone_number) {
        contact_data.push({
          "Contact Type": "Home Telephone",
          "Contact Value": this.home_phone_number,
          "Information Date": "N/A",
          "Status": "Available"
        });
      }
      
      // Add work telephone if available
      if (this.work_telephone) {
        contact_data.push({
          "Contact Type": "Work Telephone",
          "Contact Value": this.work_telephone,
          "Information Date": "N/A",
          "Status": "Available"
        });
      }
      
      // Add cell numbers
      this.cell_numbers.forEach((cell) => {
        contact_data.push({
          "Contact Type": "Cell Phone",
          "Contact Value": cell.Number || 'N/A',
          "Information Date": this.formatDate(cell.InformationDate) || 'N/A',
          "Status": "Available"
        });
      });
      
      // Add addresses
      this.addresses.forEach((address) => {
        const address_line = this.format_address_line(address);
        contact_data.push({
          "Contact Type": "Address",
          "Contact Value": address_line,
          "Information Date": this.formatDate(address.InformationDate) || 'N/A',
          "Status": "Available",
          "City": address.City || 'N/A',
          "Province": address.Province || 'N/A',
          "Postal Code": address.PostalCode || 'N/A',
          "Suburb": address.Suburb || 'N/A'
        });
      });
      
      return contact_data;
    },
    
    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          application_id: this.application_id,
          generated_date: this.formatted_generated_date,
          export_date: new Date().toISOString(),
          report_type: 'Eligibility Solution Verification Report'
        },
        raw_eligibility_data: this.safe_eligibility_data
      };
    },
    
    // Utility methods for scorecard analysis
    get_score_range(score) {
      const scoreNum = parseInt(score);
      if (scoreNum >= 700) return '700-850 (Excellent)';
      if (scoreNum >= 600) return '600-699 (Good)';
      if (scoreNum >= 500) return '500-599 (Fair)';
      if (scoreNum >= 400) return '400-499 (Poor)';
      return '0-399 (Very Poor)';
    },
    
    get_risk_level(score) {
      const scoreNum = parseInt(score);
      if (scoreNum >= 700) return 'Low Risk';
      if (scoreNum >= 600) return 'Medium-Low Risk';
      if (scoreNum >= 500) return 'Medium Risk';
      if (scoreNum >= 400) return 'Medium-High Risk';
      return 'High Risk';
    },
    
    // Validation method to test export data completeness
    validate_export_data() {
      const validation_report = {
        personal_information: {
          available: !!this.full_name && !!this.id_number,
          data_count: this.prepare_personal_information_data().length
        },
        ivs_rules: {
          available: Object.keys(this.ivs_rules_list).length > 0,
          data_count: this.prepare_ivs_rules_data().length
        },
        policy_filters: {
          available: this.policy_filters_list.length > 0,
          data_count: this.prepare_policy_filters_data().length
        },
        scorecards: {
          available: !!this.scorecard_name,
          data_count: this.prepare_scorecards_data().length
        },
        contact_details: {
          available: this.addresses.length > 0 || this.cell_numbers.length > 0 || !!this.email_address,
          data_count: this.prepare_contact_details_data().length
        }
      };
      
      return validation_report;
    },
    
    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split('T')[0];
      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: 'personal_information',
          label: 'Personal_Information',
          prepare: this.prepare_personal_information_data
        },
        {
          key: 'ivs_rules',
          label: 'IVS_Rules',
          prepare: this.prepare_ivs_rules_data
        },
        {
          key: 'policy_filters',
          label: 'Policy_Filters',
          prepare: this.prepare_policy_filters_data
        },
        {
          key: 'scorecards',
          label: 'Scorecards',
          prepare: this.prepare_scorecards_data
        },
        {
          key: 'contact_details',
          label: 'Contact_Details',
          prepare: this.prepare_contact_details_data
        }
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `Eligibility_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, 'text/csv');
          }
        }
      }
    },
    
    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split('T')[0];
      const workbook = XLSX.utils.book_new();
      
      if (this.export_options.personal_information) {
        const personal_data = this.prepare_personal_information_data();
        const worksheet = XLSX.utils.json_to_sheet(personal_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Personal Information');
      }
      
      if (this.export_options.ivs_rules) {
        const ivs_data = this.prepare_ivs_rules_data();
        const worksheet = XLSX.utils.json_to_sheet(ivs_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'IVS Rules');
      }
      
      if (this.export_options.policy_filters) {
        const policy_data = this.prepare_policy_filters_data();
        const worksheet = XLSX.utils.json_to_sheet(policy_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Policy Filters');
      }
      
      if (this.export_options.scorecards) {
        const scorecard_data = this.prepare_scorecards_data();
        const worksheet = XLSX.utils.json_to_sheet(scorecard_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Scorecards');
      }
      
      if (this.export_options.contact_details) {
        const contact_data = this.prepare_contact_details_data();
        const worksheet = XLSX.utils.json_to_sheet(contact_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Contact Details');
      }
      
      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            'JSON Data': JSON.stringify(json_data, null, 2)
          }
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Raw JSON Data');
      }
      
      const filename = `Eligibility_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
    
    async export_json() {
      const current_date = new Date().toISOString().split('T')[0];
      const export_data = {};
      
      if (this.export_options.personal_information) {
        export_data.personal_information = this.prepare_personal_information_data()[0];
      }
      
      if (this.export_options.ivs_rules) {
        export_data.ivs_rules = this.prepare_ivs_rules_data();
      }
      
      if (this.export_options.policy_filters) {
        export_data.policy_filters = this.prepare_policy_filters_data();
      }
      
      if (this.export_options.scorecards) {
        export_data.scorecards = this.prepare_scorecards_data()[0];
      }
      
      if (this.export_options.contact_details) {
        export_data.contact_details = this.prepare_contact_details_data();
      }
      
      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }
      
      const json = JSON.stringify(export_data, null, 2);
      const filename = `Eligibility_Report_${current_date}.json`;
      this.download_file(json, filename, 'application/json');
    },
    
    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};

// Add some basic CSS for status badges if not defined elsewhere
const style = document.createElement('style');
style.textContent = `
  .status-good {
    background-color: #28a745 !important;
    color: white !important;
  }
  .status-bad {
    background-color: #dc3545 !important;
    color: white !important;
  }
  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
  }
  .label-modern {
    font-size: 0.75rem;
    color: #6c757d;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;
if (!document.head.querySelector('style[data-eligibility-styles]')) {
  style.setAttribute('data-eligibility-styles', 'true');
  document.head.appendChild(style);
}
